"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

import { STORE_LOCATOR_BUILDER_COMPONENT_NAME } from "./StoreLocator.constants";

export const storeLocatorInputs = [
  {
    name: "defaultLatitude",
    type: "number",
    required: true,
    defaultValue: 0,
  },
  {
    name: "defaultLongitude",
    type: "number",
    required: true,
    defaultValue: 0,
  },
  {
    name: "storesLimit",
    type: "number",
    defaultValue: 0,
  },
  {
    name: "filtersEnabled",
    type: "boolean",
    defaultValue: true,
  },
  {
    name: "defaultZoom",
    type: "number",
    defaultValue: 15,
  },
  {
    name: "storeLocatorDetailsPageUrl",
    type: "url",
  },
  {
    name: "storeDetailsPageTarget",
    type: "string",
    enum: ["sameTab", "newTab"],
    defaultValue: "_blank",
  },
  {
    name: "componentLevelLanguage",
    type: "string",
    required: false,
    friendlyName: "Component level language override",
  },
  {
    name: "isSearchVisible",
    type: "boolean",
    defaultValue: true,
    friendlyName: "Show text input for search",
  },
  {
    name: "isInputExtraTextVisible",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show extra text for text input for search",
  },
  {
    name: "isOpeningHoursEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show shop opening hours",
  },
  {
    name: "isOpeningHoursBreaksEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show shop opening hours with breaks",
  },
  {
    name: "isAvailableDevicesEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show available device colors",
  },
  {
    name: "isAvailableSticksEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show available stick flavours",
  },
  {
    name: "isMultiBrandSticksEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show multi-brand stick flavours",
  },
  {
    name: "isFiltersSharingEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Enable user to save store filters and map position in URL",
  },
  {
    name: "isMapPositionFromUrlEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Allow URL lat/lng/zoom to set the initial map position",
  },
  {
    name: "isGeneralAnnouncementEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show general announcement for shop",
  },
  {
    name: "isAvailableServicesEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show available services",
  },
  {
    name: "isStoreTypeEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show store type on store card",
  },
  {
    name: "isVendingMachineEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show available vending machine items",
  },
  {
    name: "isVendingMachineTipEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show Vending Machine tip on store card",
  },
  {
    name: "isMapInteractionEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Toggles hiding the search bar during map interactions on mobile",
  },
  {
    name: "defaultStoreIcon",
    type: "file",
    friendlyName: "Brand store icon",
    allowedFileTypes: ["svg"],
  },
  {
    name: "isMultipleFiltersSectionOpenEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Enable user to open multiple filters section on drawer and mobile view",
  },
  {
    name: "defaultOpenedFilterCategory",
    type: "string",
    enum: ["product_types", "ProductFlavours", "storetype", "services"],
    friendlyName: "Select default opened filter section on drawer and mobile view",
  },
  {
    name: "enabledFiltersCategories",
    type: "object",
    subFields: [
      {
        name: "isOpenNowFilterEnabled",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "isProductTypesFilterEnabled",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "isStoreServicesFilterEnabled",
        type: "boolean",
        defaultValue: true,
      },
      {
        name: "isProductFlavoursFilterEnabled",
        type: "boolean",
        defaultValue: false,
      },
      {
        name: "isProductFlavoursLYOFilterEnabled",
        type: "boolean",
        defaultValue: false,
        friendlyName: "Is multi-product flavours enabled",
      },
      {
        name: "isStoreTypeFilterEnabled",
        type: "boolean",
        defaultValue: false,
      },
    ],
  },
  {
    name: "isNearbyShopsEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show nearby shops",
  },
  {
    name: "isAsianAddressFormatEnabled",
    type: "boolean",
    defaultValue: false,
    friendlyName: "Show Asian address format",
  },
  {
    name: "filtersTranslations",
    type: "list",
    subFields: [
      {
        name: "filterKey",
        type: "string",
        required: true,
      },
      {
        name: "translatedFilterKey",
        type: "string",
        required: true,
      },
    ],
  },
] as const satisfies Inputs;

export const StoreLocatorComponent: RegisteredComponent = {
  name: STORE_LOCATOR_BUILDER_COMPONENT_NAME,
  component: dynamic(
    async () =>
      withValidation(
        storeLocatorInputs,
        (await import("./StoreLocator")).StoreLocator,
        STORE_LOCATOR_BUILDER_COMPONENT_NAME
      ),
    { ssr: false }
  ),
  inputs: storeLocatorInputs,
};
